import { keyBy } from 'lodash-es';
import { defineStore } from 'pinia';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';

const transmittal_status = [
  {
    name: 'Pending review',
    slug: 'pending_review',
    class: 'bg-gray-400',
    color: '#98A2B3',
    disabled: true,
    active: null,
  },
  {
    name: 'Reviewed',
    slug: 'reviewed',
    class: 'bg-primary-600',
    color: '#1570EF',
    active: null,
  },
  {
    name: 'Approved',
    slug: 'approved',
    class: 'bg-success-600',
    color: '#039855',
    active: null,
  },
  {
    name: 'Approve with comments',
    slug: 'approved_with_comments',
    class: 'bg-green-300',
    color: '#73E2A3',
    active: null,
  },
  {
    name: 'Revise & Resubmit',
    slug: 'revise_resubmit',
    class: 'bg-orange-400',
    color: '#F38744',
    active: null,
  },
  {
    name: 'Rejected',
    slug: 'rejected',
    class: 'bg-error-500',
    color: '#F04438',
    active: null,
  },
];

export function useDMSSettingsStore(key) {
  const { $t } = useCommonImports();

  return defineStore(key ? `${key}-settings` : 'DMS-settings', {
    state: () => ({
      custom_fields: [],
      document_status: [],
      configuration: {},
      issue_purposes: [],
      token: 0,
      transmittal_status: transmittal_status.map(status => ({ ...status, name: $t(status.name) })),
    }),
    getters: {
      active_document_status: (state) => {
        return state.document_status.filter(status => status.active);
      },
      review_status_list: (state) => {
        const default_statuses = [...state.transmittal_status];
        return default_statuses.map((status) => {
          const config = state.configuration?.config || {};
          return {
            ...status,
            name: config[status.slug] || status.name,
          };
        });
      },
      document_status_map: (state) => {
        return keyBy(state.document_status, 'uid');
      },
    },
    actions: {
      async set_custom_fields(options = { query: {} }) {
        const myToken = ++this.token;
        const { data } = await this.$services.dms_custom_fields.getAll({
          query: options.query,
        });
        if (myToken !== this.token)
          return; // ignore stale
        this.custom_fields = data.custom_fields;
      },
      async set_custom_fields_public(options = { query: {} }) {
        const { data } = await this.$services.dms_custom_fields.getAll({
          attribute: 'public',
          query: options.query,
        });
        this.custom_fields = data.custom_fields;
      },
      async add_custom_fields(payload) {
        const { data } = await this.$services.dms_custom_fields.post({
          body: { custom_fields: payload },
        });
        this.custom_fields = [...this.custom_fields, ...(data.custom_fields || [])];
      },
      async update_custom_fields(uid, index, payload = {}) {
        const { data } = await this.$services.dms_custom_fields.patch({
          id: `${uid}`,
          body: { custom_fields: payload },
        });
        this.custom_fields[index] = data.custom_fields[0];
      },
      async delete_custom_fields(id, index) {
        await this.$services.dms_custom_fields.delete({
          id,
        });
        this.custom_fields.splice(index, 1);
      },
      async activate_custom_fields(uid) {
        await this.$services.dms_custom_fields.post({
          attribute: `${uid}/activate`,
        });
      },
      async deactivate_custom_fields(uid) {
        await this.$services.dms_custom_fields.post({
          attribute: `${uid}/deactivate`,
        });
      },
      async fetch_all_issue_purposes() {
        const { data } = await this.$services.dms_issue_purposes.getAll({
        });
        this.issue_purposes = data.issuePurposes;
        return data.issuePurposes;
      },
      async create_issue_purposes(issue_purpose) {
        try {
          const { data } = await this.$services.dms_issue_purposes.post({
            body: { issue_purpose },
          });

          if (data?.issuePurposes?.[0])
            this.issue_purposes.push(data.issuePurposes[0]);

          this.$toast({
            title: $t('New issue purpose added successfully'),
            type: 'success',
          });

          return data.issuePurposes;
        }
        catch (error) {
          this.$toast({
            title: $t('Something went wrong'),
            text: error.data?.description ?? '',
            type: 'error',
          });
          return error;
        }
      },
      async update_issue_purposes(id, issue_purpose) {
        try {
          const { data } = await this.$services.dms_issue_purposes.patch({
            id,
            body: { issue_purpose },
          });

          return data.issuePurposes;
        }
        catch (error) {
          this.$toast({
            title: $t('Something went wrong'),
            text: error.data?.description ?? '',
            type: 'error',
          });
          return error;
        }
      },
      async delete_issue_purposes(id, payload) {
        try {
          await this.$services.dms_issue_purposes.delete({
            id,
            body: { ...payload },
          });
        }
        catch (error) {
          // Document status used in some documents error
          if (error.data?.errorCode === 'DMS_990.1') {
            return error.data;
          }
          else {
            this.$toast({
              title: 'Something went wrong',
              text: error.data?.description ?? '',
              type: 'error',
            });
          }
        }
      },
      async fetch_all_statuses() {
        const { data } = await this.$services.dms_document_statuses.getAll();

        this.document_status = data.document_statuses;

        return data.document_statuses;
      },
      async create_document_statuses(document_status) {
        try {
          const { data } = await this.$services.dms_document_statuses.post({
            body: { document_status },
          });

          this.$toast({
            title: $t('New document status added successfully'),
            type: 'success',
          });

          return data.document_statuses;
        }
        catch (error) {
          logger.error(error);
          this.$toast({
            title: 'Something went wrong',
            text: error.data?.description ?? '',
            type: 'error',
          });
          return error;
        }
      },
      async update_document_status(id, document_status) {
        try {
          const { data } = await this.$services.dms_document_statuses.patch({
            id,
            body: { document_status },
          });

          return data.document_statuses;
        }
        catch (error) {
          logger.error(error);
          this.$toast({
            title: 'Something went wrong',
            text: error.data?.description ?? '',
            type: 'error',
          });
          return error;
        }
      },
      async delete_document_status(id, payload) {
        try {
          await this.$services.dms_document_statuses.delete({
            id,
            body: { ...payload },
          });
        }
        catch (error) {
          // Document status used in some documents error
          if (error.data?.errorCode === 'DMS_990.1') {
            return error.data;
          }
          else {
            this.$toast({
              title: 'Something went wrong',
              text: error.data?.description ?? '',
              type: 'error',
            });
          }
        }
      },
      async activate_document_status(uid) {
        await this.$services.dms_document_statuses.post({
          attribute: `${uid}/activate`,
        });
      },
      async deactivate_document_status(uid) {
        await this.$services.dms_document_statuses.post({
          attribute: `${uid}/deactivate`,
        });
      },
      async set_configuration() {
        const { data } = await this.$services.dms.getAll({
          attribute: 'configuration',
          query: {
            allowed_in: 'organization',
          },
        });
        if (data?.configuration?.length)
          this.configuration = data?.configuration[0];
        else
          this.configuration = {};
      },
      async add_configuration(payload) {
        try {
          const { data } = await this.$services.dms_configuration.post({
            body: { configuration: payload },
          });
          if (data?.configuration?.length) {
            this.configuration = data?.configuration[0];
            this.$toast({
              title: $t('Configuration has been created successfully'),
              type: 'success',
            });
          }
        }
        catch (error) {
          logger.error(error);
          this.$toast({
            title: 'Something went wrong',
            text: error?.data?.description ?? '',
            type: 'error',
          });
        }
      },
      async update_configuration(id, payload) {
        try {
          const { data } = await this.$services.dms_configuration.patch({
            id,
            body: { configuration: payload },
          });
          if (data?.configuration?.length) {
            this.configuration = data.configuration[0];
            this.$toast({
              title: $t('Configuration has been updated successfully'),
              type: 'success',
            });
          }
        }
        catch (error) {
          logger.error(error);
          this.$toast({
            title: 'Something went wrong',
            text: error?.data?.description ?? '',
            type: 'error',
          });
        }
      },
    },
  })();
}
